// TitoApiTestPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Typography, 
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  LinearProgress,
  useTheme,
  Chip,
  Grid,
  Tooltip as MuiTooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  Calendar, 
  Clock, 
  Users, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle,
  DollarSign,
  Tag,
  EuroIcon
} from 'lucide-react';
import axios from 'axios';
import PastEventsAnalysis from '../components/PastEventsAnalysis';
import EventSalesChart from '../components/EventSalesChart';
import SalesInterpretation from '../components/SalesInterpretation';
import GlobalSalesComparison from '../components/GlobalSalesComparison';
import CurrentEventsSalesOverview from '../components/CurrentEventsSalesOverview';
import { getTitoPastEvents, TitoPastEventsResponse } from '../services/titoApi';
import { PastEvent } from '../types/Event';
import RecentSalesMetrics from '../components/RecentSalesMetrics';
import SalesProjection from '../components/SalesProjection';


// Ensure REACT_APP_API_URL is set in your .env file, e.g., http://localhost:5000
const API_BASE_URL = process.env.REACT_APP_API_URL;

interface TicketType {
  name: string;
  quantity: number;
  quantity_sold: number;
  price: number;
  revenue: number;
  start_at?: string;  // ADD THIS
  end_at?: string;    // ADD THIS
}

interface Release {
  title: string;
  quantity: number;
  tickets_count: number; // from API
  price: number;
  start_at?: string;  // ADD THIS
  end_at?: string;    // ADD THIS
}

interface Registration {
  completed_at: string;
  tickets_count: number;
}

interface TitoEvent {
  id: string;
  title: string;
  slug: string;
  start_date: string;
  end_date: string;
  totalSold: number;
  totalAvailable: number;
  totalRevenue: number;
  releases: Release[];
  registrations: Registration[];
  ticketTypes: TicketType[];
  banner_url?: string; // Optional field for banner image
  venue?: {
    _id: string;
    name: string;
    capacity: number;
    location: string;
  };
  venueCapacity?: number;
}

// Interface for past events that matches the API response
interface PastEventFromAPI {
  id: string;
  title: string;
  slug: string;
  start_date: string;
  end_date: string;
  totalSold: number;
  totalAvailable: number;
  totalRevenue: number;
  ticketTypes: TicketType[];
  registrations: Registration[];
}


const TitoApiTestPage: React.FC = () => {
  const [currentEvents, setCurrentEvents] = useState<TitoEvent[]>([]);
  const [pastEvents, setPastEvents] = useState<PastEventFromAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const theme = useTheme();

  // Define the default sales period (in days) if onSaleDate is not available
  const DEFAULT_SALES_PERIOD_DAYS = 30;

  // Function to calculate days until an event
  const getDaysUntilEvent = (startDate: string): number => {
    const eventDate = new Date(startDate);
    const currentDate = new Date();
    const timeDiff = eventDate.getTime() - currentDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  // Function to get sales rate per day
  const getSalesRatePerDay = (event: TitoEvent): number => {
    if (event.registrations.length === 0) return 0;
    
    // Get earliest registration date
    const dates = event.registrations.map(reg => new Date(reg.completed_at));
    const earliestDate = new Date(Math.min(...dates.map(date => date.getTime())));
    
    const now = new Date();
    const daysSinceFirstSale = Math.max(1, Math.ceil((now.getTime() - earliestDate.getTime()) / (1000 * 3600 * 24)));
    
    return event.totalSold / daysSinceFirstSale;
  };

  // Function to determine if an event needs attention based on various metrics
  const needsAttention = (event: TitoEvent): boolean => {
    const daysToEvent = getDaysUntilEvent(event.start_date);
    const salesRatio = event.totalAvailable > 0 ? event.totalSold / event.totalAvailable : 0;
    
    // Events that start soon but have low sales need attention
    if (daysToEvent <= 14 && salesRatio < 0.5) return true;
    
    // Events with no sales in the last week need attention
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const hasRecentSales = event.registrations.some(reg => 
      new Date(reg.completed_at) >= oneWeekAgo
    );
    
    if (!hasRecentSales && daysToEvent <= 30) return true;
    
    return false;
  };

const getEarlyBirdStatus = (event: TitoEvent): { 
  isEarlyBird: boolean; 
  daysRemaining: number; 
  isClosed: boolean;
  earlyBirdRelease?: TicketType;
} => {
  // Find the Early Bird ticket type
  const earlyBirdRelease = event.ticketTypes.find(tt => 
    tt.name.toLowerCase().includes('early bird')
  );
  
  if (!earlyBirdRelease || !earlyBirdRelease.start_at) {
    return { isEarlyBird: false, daysRemaining: 0, isClosed: false };
  }
  
  const now = new Date();
  const startDate = new Date(earlyBirdRelease.start_at);
  const endDate = new Date(earlyBirdRelease.start_at);
  endDate.setDate(endDate.getDate() + 7); // Early bird lasts 7 days from its start_at
  
  // Check if early bird period hasn't started yet
  if (now < startDate) {
    return { isEarlyBird: false, daysRemaining: 0, isClosed: false, earlyBirdRelease };
  }
  
  // Check if early bird period has ended
  if (now > endDate) {
    return { isEarlyBird: false, daysRemaining: 0, isClosed: true, earlyBirdRelease };
  }
  
  // Calculate days remaining (round up so last partial day shows as 1 day)
  const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  return { 
    isEarlyBird: true, 
    daysRemaining, 
    isClosed: false,
    earlyBirdRelease 
  };
};

  // Handle chart event click - expand accordion and scroll to it
  const handleChartEventClick = (eventTitle: string) => {
    // Find the event by title
    const event = currentEvents.find(e => e.title === eventTitle);
    if (!event) return;
    
    // Expand the accordion
    setExpandedAccordion(event.id);
    
    // Scroll to the accordion after a short delay to let it expand
    setTimeout(() => {
      const element = document.getElementById(`accordion-${event.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Fetch Event Details Function
  const fetchEventDetails = useCallback(async (eventSlug: string): Promise<TitoEvent | null> => {
    try {
      const [eventResponse, registrationsResponse, releasesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/tito/events/${eventSlug}`, {
          headers: {
            'Accept': 'application/json'
          }
        }),
        axios.get(`${API_BASE_URL}/api/tito/${eventSlug}/registrations`, {
          headers: {
            'Accept': 'application/json'
          }
        }),
        axios.get(`${API_BASE_URL}/api/tito/${eventSlug}/releases`, {
          headers: {
            'Accept': 'application/json'
          }
        })
      ]);


      // Adjust this if your API structure differs
      const responseData = eventResponse.data;
      const eventData = responseData.event || responseData;
      const venue = responseData.venue;
      const venueCapacity = responseData.venueCapacity;
      const registrations = registrationsResponse.data.registrations || [];
      const releases = releasesResponse.data.releases || [];

      if (!eventData.start_date || !eventData.end_date) {
        console.warn('Event missing start_date or end_date:', eventData);
      }

     const ticketTypes: TicketType[] = releases.map((release: Release) => ({
  name: release.title,
  quantity: release.quantity,
  quantity_sold: release.tickets_count,
  price: release.price,
  revenue: release.tickets_count * release.price,
  start_at: release.start_at,  // ADD THIS
  end_at: release.end_at        // ADD THIS
}));

      const totalSold = ticketTypes.reduce((sum, type) => sum + type.quantity_sold, 0);
      const totalAvailable = ticketTypes.reduce((sum, type) => sum + type.quantity, 0);
      const totalRevenue = ticketTypes.reduce((sum, type) => sum + type.revenue, 0);

      return {
        id: eventData.id,
        title: eventData.title,
        slug: eventData.slug,
        start_date: eventData.start_date,
        end_date: eventData.end_date,
        totalSold,
        totalAvailable,
        totalRevenue,
        releases,
        registrations,
        ticketTypes,
        banner_url: eventData.banner_url || '',
        venue: venue,
        venueCapacity: venueCapacity
      };
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.response?.data || error.message);
        setError(error.response?.data?.message || 'Axios error occurred');
      } else if (error instanceof Error) {
        console.error('Error:', error.message);
        setError(error.message);
      } else {
        console.error('Unexpected error:', error);
        setError('An unexpected error occurred');
      }
      return null;
    }
  }, []);

  // Fetch Current Events
  const fetchCurrentEvents = useCallback(async () => {
    try {
      // Fetch all current events from the server
      const response = await axios.get(`${API_BASE_URL}/api/tito/events`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      console.log('Raw events response:', response.data);
      const fetchedEvents = response.data.events || [];

      console.log(`Total events fetched: ${fetchedEvents.length}`);
      const currentDate = new Date();
      const currentEventsData: TitoEvent[] = [];

      for (const evt of fetchedEvents) {
        const eventWithDetails = await fetchEventDetails(evt.slug);
        if (eventWithDetails) {
          const eventEndDate = new Date(eventWithDetails.end_date);
          if (isNaN(eventEndDate.getTime())) {
            console.warn('Invalid event end_date, skipping event:', eventWithDetails.end_date);
          } else if (eventEndDate >= currentDate) {
            currentEventsData.push(eventWithDetails);
          }
        }
      }

      setCurrentEvents(currentEventsData);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.response?.data || error.message);
        setError(error.response?.data?.message || 'Axios error occurred');
      } else if (error instanceof Error) {
        console.error('Error:', error.message);
        setError(error.message);
      } else {
        console.error('Unexpected error:', error);
        setError('An unexpected error occurred');
      }
    }
  }, [fetchEventDetails]);

  // Fetch Past Events
  const fetchPastEventsData = useCallback(async () => {
    console.log('🔍 Starting fetchPastEventsData...');
    console.log('📍 API_BASE_URL:', API_BASE_URL);
    console.log('📍 Full URL:', `${API_BASE_URL}/api/tito/past/detailed`);
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tito/past/detailed`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      console.log('✅ Past events API response received');
      console.log('📦 Response status:', response.status);
      console.log('📦 Response data:', response.data);
      console.log('📦 Response data type:', typeof response.data);
      console.log('📦 Has past_events key?', 'past_events' in (response.data || {}));
      
      if (response.data && response.data.past_events) {
        console.log('✅ Found past_events array, length:', response.data.past_events.length);
        
        const fetchedPastEvents: PastEventFromAPI[] = response.data.past_events.map((event: any, index: number) => {
          console.log(`📝 Processing past event ${index + 1}:`, {
            title: event.title,
            hasRegistrations: !!(event.registrations),
            registrationsLength: event.registrations?.length || 0,
            totalSold: event.totalSold
          });
          
          return {
            id: event.id,
            title: event.title,
            slug: event.slug,
            start_date: event.start_date,
            end_date: event.end_date,
            totalSold: event.totalSold || 0,
            totalAvailable: event.totalAvailable || 0,
            totalRevenue: event.totalRevenue || 0,
            ticketTypes: event.ticketTypes || [],
            registrations: event.registrations || [] // This is the key field we need!
          };
        });

        // Log to debug the registrations data
        console.log('✅ Processed past events count:', fetchedPastEvents.length);
        const eventsWithRegs = fetchedPastEvents.filter(e => e.registrations && e.registrations.length > 0);
        console.log('📊 Past events with registrations:', eventsWithRegs.length);
        
        // Log a sample event with registrations
        const sampleEventWithRegs = fetchedPastEvents.find(e => e.registrations && e.registrations.length > 0);
        if (sampleEventWithRegs) {
          console.log('📋 Sample past event with registrations:', {
            title: sampleEventWithRegs.title,
            totalSold: sampleEventWithRegs.totalSold,
            registrationsCount: sampleEventWithRegs.registrations.length,
            sampleRegistration: sampleEventWithRegs.registrations[0]
          });
        } else {
          console.warn('⚠️ No past events have registration data!');
          console.log('First 3 events sample:', fetchedPastEvents.slice(0, 3));
        }

        console.log('💾 Setting past events state with', fetchedPastEvents.length, 'events');
        setPastEvents(fetchedPastEvents);
      } else {
        console.error('❌ Unexpected past events data structure:', response.data);
        console.log('Response keys:', Object.keys(response.data || {}));
        setPastEvents([]);
      }
    } catch (error) {
      console.error('❌ Error fetching past events:', error);
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url
        });
        
        if (error.response?.status === 429) {
          console.log('⏳ Rate limited, retrying in 2 seconds...');
          setTimeout(() => fetchPastEventsData(), 2000);
          return;
        }
      }
      setError('Failed to fetch past events data');
    }
  }, []);

  // Fetch All Events
  const fetchAllEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchCurrentEvents(),
        fetchPastEventsData()
      ]);
    } catch (err) {
      console.error('Error fetching all events:', err);
      setError('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [fetchCurrentEvents, fetchPastEventsData]);

  useEffect(() => {
    fetchAllEvents();
  }, [fetchAllEvents]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Function to determine progress bar color based on sales performance
  const getProgressColor = (
    totalSold: number,
    totalAvailable: number,
    startDate: string,
    endDate: string
  ): 'success' | 'warning' | 'error' => {
    const currentDate = new Date();
    const eventStartDate = new Date(startDate);
    const eventEndDate = new Date(endDate);

    const onSaleDate = new Date(eventStartDate);
    onSaleDate.setDate(onSaleDate.getDate() - DEFAULT_SALES_PERIOD_DAYS);

    const totalSalesPeriod = Math.ceil((eventStartDate.getTime() - onSaleDate.getTime()) / (1000 * 60 * 60 * 24));
    const daysOnSale = Math.max(1, Math.ceil((currentDate.getTime() - onSaleDate.getTime()) / (1000 * 60 * 60 * 24)));
    const effectiveDaysOnSale = Math.min(daysOnSale, totalSalesPeriod);
    const expectedSales = (effectiveDaysOnSale / totalSalesPeriod) * totalAvailable;
    const salesRatio = expectedSales > 0 ? totalSold / expectedSales : 0;

    if (salesRatio >= 0.9) return 'success';
    if (salesRatio >= 0.5) return 'warning';
    return 'error';
  };

  // Sort events by days to event (soonest first)
  const sortedCurrentEvents = [...currentEvents].sort((a, b) => {
    return getDaysUntilEvent(a.start_date) - getDaysUntilEvent(b.start_date);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ color: 'error.main', textAlign: 'center', mt: 4 }}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  console.log('Current Events:', currentEvents);
  console.log('Past Events for Projection:', pastEvents.length, 'events');
  console.log('Past Events with registration data:', 
    pastEvents.filter(e => e.registrations && e.registrations.length > 0).length
  );

  return (
    <Box sx={{ p: 3 }}>
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Current Events" />
        <Tab label="Past Events Analysis" />
      </Tabs>

      {tabValue === 0 && (
        <>
          <RecentSalesMetrics events={currentEvents} />
          <CurrentEventsSalesOverview events={currentEvents} pastEvents={pastEvents} onEventClick={handleChartEventClick} />
          
          <Typography variant="h4" sx={{ mb: 3 }}>Current Events</Typography>
          {currentEvents.length === 0 ? (
            <Typography>No current events available.</Typography>
          ) : (
            <>
              {sortedCurrentEvents.map((event: TitoEvent, index: number) => {              
                const progressColor = getProgressColor(event.totalSold, event.totalAvailable, event.start_date, event.end_date);
                const daysToEvent = getDaysUntilEvent(event.start_date);
                const salesPercentage = event.venueCapacity 
                  ? (event.totalSold / event.venueCapacity) * 100 
                  : event.totalAvailable > 0 
                    ? (event.totalSold / event.totalAvailable) * 100 
                    : 0;
                const salesRate = getSalesRatePerDay(event);
                const needsAttentionFlag = needsAttention(event);
                const avgTicketPrice = event.totalSold > 0 ? (event.totalRevenue || 0) / event.totalSold : 0;

                return (
                  <Accordion 
                    key={event.id} 
                    id={`accordion-${event.id}`}
                    expanded={expandedAccordion === event.id}
                    onChange={(e, isExpanded) => setExpandedAccordion(isExpanded ? event.id : false)}
                    sx={{ 
                      mb: 2, 
                      border: needsAttentionFlag ? `1px solid ${theme.palette.warning.main}` : 'none'
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: needsAttentionFlag ? 'rgba(255, 152, 0, 0.05)' : 'transparent',
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        {/* Title and status area */}
                        <Grid item xs={12} md={5}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ mr: 1 }}>
                                {event.title}
                              </Typography>
                              {needsAttentionFlag && (
                                <MuiTooltip title="This event needs attention">
                                  <AlertCircle size={16} color={theme.palette.warning.main} />
                                </MuiTooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              <Chip 
                                icon={<Calendar size={14} />} 
                                label={`${daysToEvent} days to event`} 
                                size="small" 
                                color={daysToEvent < 7 ? 'error' : daysToEvent < 14 ? 'warning' : 'default'}
                              />
                              <Chip 
                                icon={<EuroIcon size={14} />} 
                                label={`€${avgTicketPrice.toFixed(0)} avg`} 
                                size="small"
                              />
                            </Box>
                          </Box>
                          {(() => {
  const earlyBirdStatus = getEarlyBirdStatus(event);
  
  if (earlyBirdStatus.isEarlyBird) {
    return (
      <Chip 
        label={`Early Bird: ${earlyBirdStatus.daysRemaining} day${earlyBirdStatus.daysRemaining !== 1 ? 's' : ''} left`}
        size="small"
        color="success"
        sx={{ mt: 0.5 }}
      />
    );
  }
  
  if (earlyBirdStatus.isClosed && earlyBirdStatus.earlyBirdRelease) {
    return (
      <Chip 
        label="Early Bird Closed"
        size="small"
        color="default"
        sx={{ mt: 0.5 }}
      />
    );
  }
  
  return null;
})()}
                        </Grid>
                        
                        {/* Sales stats area */}
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                              <Users size={14} style={{ marginRight: '4px' }} />
                              {event.totalSold} / {event.venueCapacity || event.totalAvailable} sold ({salesPercentage.toFixed(0)}%)
                            </Typography>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                              <Clock size={14} style={{ marginRight: '4px' }} />
                              {salesRate.toFixed(1)} tickets/day
                              {salesRate > 0 ? (
                                <MuiTooltip title="Sales rate">
                                  <Box component="span" sx={{ ml: 0.5 }}>
                                    {salesRate > 1 ? (
                                      <TrendingUp size={14} color={theme.palette.success.main} />
                                    ) : (
                                      <TrendingDown size={14} color={theme.palette.error.main} />
                                    )}
                                  </Box>
                                </MuiTooltip>
                              ) : null}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        {/* Revenue area */}
                        <Grid item xs={12} md={3}>
                          <Box>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              Revenue: €{event.totalRevenue.toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {event.ticketTypes.slice(0, 2).map((type, idx) => (
                                <Chip 
                                  key={idx}
                                  icon={<Tag size={14} />} 
                                  label={`${type.name}: ${type.quantity_sold}/${type.quantity}`} 
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                              {event.ticketTypes.length > 2 && (
                                <Chip 
                                  label={`+${event.ticketTypes.length - 2} more`} 
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Grid>
                        
                        {/* Progress bar (full width) */}
                        <Grid item xs={12}>
                          <Box sx={{ width: '100%' }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={salesPercentage > 100 ? 100 : salesPercentage} 
                              color={progressColor}
                              sx={{ height: 10, borderRadius: 5 }}
                              aria-label={`${salesPercentage.toFixed(1)}% of tickets sold for ${event.title}`}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                         <Box sx={{ mb: 3 }}>
  <Typography variant="h6" gutterBottom>Key Metrics</Typography>
  
  {/* Stat Cards Grid */}
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {/* Daily Sales Rate */}
    <Grid item xs={6} md={4}>
      <Box sx={{ 
        p: 2, 
        border: '1px solid rgba(255, 255, 255, 0.1)', 
        borderRadius: 2, 
        textAlign: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
          <TrendingUp size={20} color={salesRate > 1 ? '#4caf50' : '#f44336'} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {salesRate.toFixed(1)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          tickets/day
        </Typography>
      </Box>
    </Grid>

    {/* Total Sold */}
    <Grid item xs={6} md={4}>
      <Box sx={{ 
        p: 2, 
        border: '1px solid rgba(255, 255, 255, 0.1)', 
        borderRadius: 2, 
        textAlign: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
          <Users size={20} color="#1976d2" />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {event.totalSold}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Total Sold
        </Typography>
      </Box>
    </Grid>

    {/* Days On Sale */}
    <Grid item xs={6} md={4}>
      <Box sx={{ 
        p: 2, 
        border: '1px solid rgba(255, 255, 255, 0.1)', 
        borderRadius: 2, 
        textAlign: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
          <Clock size={20} color="#ff9800" />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {event.registrations.length > 0 ? Math.ceil((new Date().getTime() - Math.min(...event.registrations.map(r => new Date(r.completed_at).getTime()))) / (1000 * 60 * 60 * 24)) : 0}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Days On Sale
        </Typography>
      </Box>
    </Grid>

    {/* Days Until Event */}
    <Grid item xs={6} md={4}>
      <Box sx={{ 
        p: 2, 
        border: '1px solid rgba(255, 255, 255, 0.1)', 
        borderRadius: 2, 
        textAlign: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
          <Calendar size={20} color={daysToEvent < 7 ? '#f44336' : daysToEvent < 14 ? '#ff9800' : '#4caf50'} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {daysToEvent}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Days Until Event
        </Typography>
      </Box>
    </Grid>

    {/* Tickets vs Capacity */}
    <Grid item xs={12} md={8}>
      <Box sx={{ 
        p: 2, 
        border: '1px solid rgba(255, 255, 255, 0.1)', 
        borderRadius: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Tickets vs Capacity
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {event.totalSold} / {event.venueCapacity || event.totalAvailable}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={event.venueCapacity ? (event.totalSold / event.venueCapacity) * 100 : event.totalAvailable > 0 ? (event.totalSold / event.totalAvailable) * 100 : 0} 
          sx={{ height: 8, borderRadius: 5, mb: 0.5 }}
          color={salesPercentage > 80 ? 'success' : salesPercentage > 50 ? 'warning' : 'error'}
        />
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', display: 'block' }}>
          {event.venueCapacity ? ((event.totalSold / event.venueCapacity) * 100).toFixed(1) : event.totalAvailable > 0 ? ((event.totalSold / event.totalAvailable) * 100).toFixed(1) : 0}% capacity
        </Typography>
      </Box>
    </Grid>
  </Grid>

  {/* Additional Details - Compact */}
  <Box sx={{ 
    p: 2, 
    border: '1px solid rgba(255, 255, 255, 0.1)', 
    borderRadius: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.05)'
  }}>
    <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
      Additional Details
    </Typography>
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">Venue:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>{event.venue?.name || 'N/A'}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">Venue Capacity:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>{event.venueCapacity || 'N/A'}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">Start Date:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>{new Date(event.start_date).toLocaleDateString()}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">End Date:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>{new Date(event.end_date).toLocaleDateString()}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">On Sale Date:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {event.registrations.length > 0 ? new Date(Math.min(...event.registrations.map(r => new Date(r.completed_at).getTime()))).toLocaleDateString() : 'No sales yet'}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">Total Available:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>{event.totalAvailable}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">Total Revenue:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>€{event.totalRevenue.toFixed(2)}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">Registrations:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>{event.registrations.length} records</Typography>
      </Grid>
    </Grid>
  </Box>
</Box>          
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>Ticket Types</Typography>
                            {event.ticketTypes.map((type: TicketType, typeIndex: number) => (
                              <Box key={typeIndex} sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                <Typography variant="subtitle2">{type.name}</Typography>
                                <Typography>Sold: {type.quantity_sold} / {type.quantity}</Typography>
                                <Typography>Price: €{(type.price || 0).toFixed(2)}</Typography>
                                <Typography>Revenue: €{(type.revenue || 0).toFixed(2)}</Typography>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={type.quantity > 0 ? (type.quantity_sold / type.quantity) * 100 : 0} 
                                  sx={{ height: 5, borderRadius: 5, mt: 1 }}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={8}>
                          <EventSalesChart event={event} />
                          <SalesProjection currentEvent={event} pastEvents={pastEvents} />
                          <SalesInterpretation event={event} pastEvents={pastEvents} />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
              
              {/* Global comparison after all events */}
              <Box sx={{ mt: 4 }}>
                <GlobalSalesComparison events={currentEvents} />
              </Box>
            </>
          )}
        </>
      )}

      {tabValue === 1 && (
        <>
          {pastEvents.length === 0 ? (
            <Box sx={{ color: 'warning.main', textAlign: 'center', mt: 4 }}>
              <Alert severity="warning">
                No valid past event data available. Total events received: {pastEvents.length}. 
                Please check the data format and ensure all required fields are present.
              </Alert>
              <Typography variant="h6" sx={{ mt: 2 }}>Debug Information:</Typography>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(pastEvents, null, 2)}
              </pre>
            </Box>
          ) : (
            <PastEventsAnalysis pastEvents={pastEvents} />
          )}
        </>
      )}
    </Box>
  );
};

export default TitoApiTestPage;